import { Search } from "lucide-react";

import { Button } from "@/common/ui/button";
import { focusOutline } from "@/common/ui/focus-outline";
import { Input } from "@/common/ui/input";

import type { GuardrailMetricsFilters } from "./use-guardrail-metrics";

interface MetricsToolbarProps {
  filters: GuardrailMetricsFilters;
  onFiltersChange: (filters: GuardrailMetricsFilters) => void;
  onSearch: () => void;
  isLoading: boolean;
}

const POLICY_OPTIONS = [
  { label: "All Policies", value: "" },
  { label: "Topic Policy", value: "topicPolicy" },
  { label: "Content Policy", value: "contentPolicy" },
  { label: "Word Policy", value: "wordPolicy" },
  { label: "Sensitive Info", value: "sensitiveInformationPolicy" },
  { label: "Grounding", value: "contextualGroundingPolicy" },
];

const ENV_OPTIONS = [
  { label: "All Environments", value: "" },
  { label: "Dev", value: "dev" },
  { label: "Integration", value: "integration" },
  { label: "Staging", value: "staging" },
  { label: "Production", value: "production" },
];

export function MetricsToolbar({
  filters,
  onFiltersChange,
  onSearch,
  isLoading,
}: MetricsToolbarProps): React.ReactElement {
  function updateFilter<K extends keyof GuardrailMetricsFilters>(
    key: K,
    value: GuardrailMetricsFilters[K]
  ): void {
    onFiltersChange({ ...filters, [key]: value });
  }

  return (
    <div data-testid="metrics-toolbar" className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="start-time" className="text-xs font-medium text-muted-foreground">
            Start
          </label>
          <Input
            id="start-time"
            data-testid="filter-start-time"
            type="datetime-local"
            value={filters.startTime}
            onChange={(e) => updateFilter("startTime", e.target.value)}
            className="w-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="end-time" className="text-xs font-medium text-muted-foreground">
            End
          </label>
          <Input
            id="end-time"
            data-testid="filter-end-time"
            type="datetime-local"
            value={filters.endTime}
            onChange={(e) => updateFilter("endTime", e.target.value)}
            className="w-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="env-filter" className="text-xs font-medium text-muted-foreground">
            Environment
          </label>
          <select
            id="env-filter"
            data-testid="filter-environment"
            value={filters.environment}
            onChange={(e) => updateFilter("environment", e.target.value)}
            className={`flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ${focusOutline}`}
          >
            {ENV_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="policy-filter" className="text-xs font-medium text-muted-foreground">
            Policy
          </label>
          <select
            id="policy-filter"
            data-testid="filter-policy"
            value={filters.policy}
            onChange={(e) => updateFilter("policy", e.target.value)}
            className={`flex h-9 w-48 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ${focusOutline}`}
          >
            {POLICY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <Button
          data-testid="search-metrics-button"
          size="sm"
          onClick={onSearch}
          disabled={isLoading}
        >
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>
    </div>
  );
}
